/**
 * Presentation helpers for the quote leveling board and award decision UI.
 * Everything here formats or labels backend-provided values — no commercial
 * arithmetic happens in the browser.
 */

export const fmtMoney = (value) => {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * The separately-reported answers to "who is lowest?". Order matters: it is
 * the order in which scope, completeness, conformance, and qualification
 * peel bidders away from the raw number. lowestResponsiveQuoteId arrived
 * with the RFQ operating API; older leveling payloads omit it, so renderers
 * filter to the keys actually present.
 */
export const LOWEST_KEYS = [
  {
    key: "lowestSubmittedQuoteId",
    label: "Lowest submitted",
    hint: "Smallest raw number as written by the bidder — before scope, completeness, or qualification are considered.",
  },
  {
    key: "lowestNormalizedQuoteId",
    label: "Lowest normalized",
    hint: "Cheapest once evidenced adjustments bring every offer onto the same scope.",
  },
  {
    key: "lowestCompleteQuoteId",
    label: "Lowest complete",
    hint: "Cheapest among quotes that answered every question asked.",
  },
  {
    key: "lowestResponsiveQuoteId",
    label: "Lowest responsive",
    hint: "Cheapest among conforming quotes — complete, no disqualifying gaps.",
  },
  {
    key: "lowestResponsiveQualifiedQuoteId",
    label: "Lowest responsive & qualified",
    hint: "Cheapest among conforming quotes that also meet the schedule requirement — the defensible commercial answer.",
  },
];

/** The keys this leveling payload actually reports (contract-version tolerant). */
export const presentLowestKeys = (leveling) =>
  LOWEST_KEYS.filter(({ key }) => leveling && key in leveling);

/** Chips that apply to one quote card, derived from the leveling result. */
export const quoteDistinctions = (leveling, quoteId) =>
  presentLowestKeys(leveling)
    .filter(({ key }) => leveling?.[key] === quoteId)
    .map(({ label }) => label);

export const COMPLIANCE_LABELS = {
  NOT_EVALUATED: "Not evaluated",
  COMPLIANT: "Compliant",
  COMPLIANT_WITH_EXCEPTIONS: "Compliant with exceptions",
  NON_RESPONSIVE: "Non-responsive",
  DISQUALIFIED: "Disqualified",
};

export const PROPOSAL_STATE_STYLES = {
  PROPOSED: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  APPROVED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  AUTHORIZED_TO_PROCEED: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  REJECTED: "bg-red-500/20 text-red-400 border-red-500/30",
  REVISION_REQUESTED: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  CLARIFICATION_REQUESTED: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  SUPERSEDED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  WITHDRAWN: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

export const MANDATORY_KINDS = [
  "SCOPE",
  "ALTERNATE",
  "BONDING",
  "INSURANCE",
  "LICENSE",
  "CERTIFICATION",
  "SCHEDULE",
];
